import { Controller, UseFormReturn } from "react-hook-form";
import { CompensationProfileFormInput } from "../types";
import ListItem from "@/components/ListItem";
import Checkbox from "@/components/Checkbox";
import { NumberFormatBase } from "react-number-format";
import TextField from "@/components/TextField";
import { currencyFormatterNoDecimals } from "@/lib/currencyHelpers";

export default function BenefitDetailsStep({ form }: { form: UseFormReturn<CompensationProfileFormInput> }) {
  return (
    <div className="space-y-6">
      <div className="text-headline-small">Enter benefit details (breakdown of compensation summary).</div>
      <div className="col-span-full">
        <p className="text-title-medium mb-2">Check All that Apply</p>
        <ListItem
          headline="My employer offers a 401k"
          leading={
            <Checkbox
              name="employerOffers401k"
              checked={form.getValues("employerOffers401k")}
              onChange={(checked) => {
                form.setValue("employerOffers401k", checked);
              }}
            />
          }
        />
        <div className="ps-[40px]">
          <ListItem
            headline="My employer matches my 401k"
            leading={
              <Checkbox
                disabled={!form.watch("employerOffers401k")}
                name="employerMatches401k"
                checked={form.getValues("employerMatches401k")}
                onChange={(checked) => {
                  form.setValue("employerMatches401k", checked);
                }}
              />
            }
          />
          <div className="ps-[40px]">
            <Controller
              control={form.control}
              name="employerMatch401kPercent"
              render={({ field }) => (
                <NumberFormatBase
                  value={field.value}
                  disabled={!form.watch("employerMatches401k")}
                  name="employerMatch401kPercent"
                  className="w-full"
                  customInput={TextField}
                  error={!!form.formState.errors.employerMatch401kPercent}
                  errorText={form.formState.errors.employerMatch401kPercent?.message as string}
                  onFocus={(e) => {
                    e.target.select();
                  }}
                  onValueChange={(values) => {
                    field.onChange(values.value ? parseInt(values.value, 10) : null);
                    form.trigger("employerMatch401kPercent");
                  }}
                  leadingIcon="percent"
                  label="Percent of 401k Match"
                  placeholder="Percent of 401k Match"
                />
              )}
            />
          </div>
        </div>
        <ListItem
          headline="My employer pays my PGA dues"
          leading={
            <Checkbox
              name="employerPaysPgaDues"
              checked={form.getValues("employerPaysPgaDues")}
              onChange={(checked) => {
                form.setValue("employerPaysPgaDues", checked);
              }}
            />
          }
        />
        <ListItem
          headline="My employer offers healthcare coverage"
          leading={
            <Checkbox
              name="employerOffersHealthcare"
              checked={form.getValues("employerOffersHealthcare")}
              onChange={(checked) => {
                form.setValue("employerOffersHealthcare", checked);
              }}
            />
          }
        />
        <div className="ps-[40px]">
          <ListItem
            headline="I use my employer offered healthcare coverage"
            leading={
              <Checkbox
                disabled={!form.watch("employerOffersHealthcare")}
                name="employerOffersHealthcareUsed"
                checked={form.getValues("employerOffersHealthcareUsed")}
                onChange={(checked) => {
                  form.setValue("employerOffersHealthcareUsed", checked);
                }}
              />
            }
          />
        </div>
        <ListItem
          headline="My employer offers dental insurance"
          leading={
            <Checkbox
              name="employerOffersDental"
              checked={form.getValues("employerOffersDental")}
              onChange={(checked) => {
                form.setValue("employerOffersDental", checked);
              }}
            />
          }
        />
        <ListItem
          headline="My employer offers vision insurance"
          leading={
            <Checkbox
              name="employerOffersVision"
              checked={form.getValues("employerOffersVision")}
              onChange={(checked) => {
                form.setValue("employerOffersVision", checked);
              }}
            />
          }
        />
        <ListItem
          headline="My employer offers life insurance"
          leading={
            <Checkbox
              name="employerOffersLifeInsurance"
              checked={form.getValues("employerOffersLifeInsurance")}
              onChange={(checked) => {
                form.setValue("employerOffersLifeInsurance", checked);
              }}
            />
          }
        />
        <ListItem
          headline="My employer offers disability insurance"
          leading={
            <Checkbox
              name="employerOffersDisability"
              checked={form.getValues("employerOffersDisability")}
              onChange={(checked) => {
                form.setValue("employerOffersDisability", checked);
              }}
            />
          }
        />
        <ListItem
          headline="My employer offers PTO"
          leading={
            <Checkbox
              name="employerOffersPaidVacation"
              checked={form.getValues("employerOffersPaidVacation")}
              onChange={(checked) => {
                form.setValue("employerOffersPaidVacation", checked);
              }}
            />
          }
        />

        <div className="ps-[40px]">
          <Controller
            control={form.control}
            name="employerOffersPaidVacationWeekAmount"
            rules={{
              min: {
                value: 0,
                message: "Annual Weeks of PTO must be greater than or equal to 0"
              },
              max: {
                value: 52,
                message: "Annual Weeks of PTO must be less than or equal to 52"
              }
            }}
            render={({ field }) => (
              <NumberFormatBase
                value={field.value}
                disabled={!form.watch("employerOffersPaidVacation")}
                name="employerOffersPaidVacationWeekAmount"
                className="w-full"
                customInput={TextField}
                error={!!form.formState.errors.employerOffersPaidVacationWeekAmount}
                errorText={form.formState.errors.employerOffersPaidVacationWeekAmount?.message as string}
                onFocus={(e) => {
                  e.target.select();
                }}
                onValueChange={(values) => {
                  field.onChange(values.value ? parseInt(values.value, 10) : null);
                  form.trigger("employerOffersPaidVacationWeekAmount");
                }}
                label="Annual Weeks of PTO"
                placeholder="Annual Weeks of PTO"
              />
            )}
          />
        </div>

        <ListItem
          headline="My employer offers education allowance"
          leading={
            <Checkbox
              name="employerOffersEducationAllowance"
              checked={form.getValues("employerOffersEducationAllowance")}
              onChange={(checked) => {
                form.setValue("employerOffersEducationAllowance", checked);
              }}
            />
          }
        />
        <div className="ps-[40px]">
          <Controller
            control={form.control}
            name="employerOffersEducationAllowanceAmount"
            render={({ field }) => (
              <NumberFormatBase
                value={field.value}
                disabled={!form.watch("employerOffersEducationAllowance")}
                name="employerOffersEducationAllowanceAmount"
                className="w-full"
                customInput={TextField}
                error={!!form.formState.errors.employerOffersEducationAllowanceAmount}
                errorText={form.formState.errors.employerOffersEducationAllowanceAmount?.message as string}
                onFocus={(e) => {
                  e.target.select();
                }}
                format={currencyFormatterNoDecimals}
                onValueChange={(values) => {
                  field.onChange(values.value ? parseInt(values.value, 10) : null);
                  form.trigger("employerOffersEducationAllowanceAmount");
                }}
                leadingIcon="attach_money"
                label="Amount of Education Allowance"
                placeholder="Amount of Education Allowance"
              />
            )}
          />
        </div>
        <ListItem
          headline="My employer offers tournament allowance"
          leading={
            <Checkbox
              name="employerOffersTournamentAllowance"
              checked={form.getValues("employerOffersTournamentAllowance")}
              onChange={(checked) => {
                form.setValue("employerOffersTournamentAllowance", checked);
              }}
            />
          }
        />
        <div className="ps-[40px]">
          <Controller
            control={form.control}
            name="employerOffersTournamentAllowanceAmount"
            render={({ field }) => (
              <NumberFormatBase
                value={field.value}
                disabled={!form.watch("employerOffersTournamentAllowance")}
                name="employerOffersTournamentAllowanceAmount"
                className="w-full"
                customInput={TextField}
                error={!!form.formState.errors.employerOffersTournamentAllowanceAmount}
                errorText={form.formState.errors.employerOffersTournamentAllowanceAmount?.message as string}
                onFocus={(e) => {
                  e.target.select();
                }}
                format={currencyFormatterNoDecimals}
                onValueChange={(values) => {
                  field.onChange(values.value ? parseInt(values.value, 10) : null);
                  form.trigger("employerOffersTournamentAllowanceAmount");
                }}
                leadingIcon="attach_money"
                label="Amount of Tournament Allowance"
                placeholder="Amount of Tournament Allowance"
              />
            )}
          />
        </div>
        <ListItem
          headline="My employer offers cellphone allowance"
          leading={
            <Checkbox
              name="employerOffersCellphoneAllowance"
              checked={form.getValues("employerOffersCellphoneAllowance")}
              onChange={(checked) => {
                form.setValue("employerOffersCellphoneAllowance", checked);
              }}
            />
          }
        />
        <div className="ps-[40px]">
          <Controller
            control={form.control}
            name="employerOffersCellphoneAllowanceAmount"
            render={({ field }) => (
              <NumberFormatBase
                value={field.value}
                disabled={!form.watch("employerOffersCellphoneAllowance")}
                name="employerOffersCellphoneAllowanceAmount"
                className="w-full"
                customInput={TextField}
                error={!!form.formState.errors.employerOffersCellphoneAllowanceAmount}
                errorText={form.formState.errors.employerOffersCellphoneAllowanceAmount?.message as string}
                onFocus={(e) => {
                  e.target.select();
                }}
                format={currencyFormatterNoDecimals}
                onValueChange={(values) => {
                  field.onChange(values.value ? parseInt(values.value, 10) : null);
                  form.trigger("employerOffersCellphoneAllowanceAmount");
                }}
                leadingIcon="attach_money"
                label="Amount of Cellphone Allowance"
                placeholder="Amount of Cellphone Allowance"
              />
            )}
          />
        </div>
        <ListItem
          headline="My employer offers car allowance"
          leading={
            <Checkbox
              name="employerOffersCarAllowance"
              checked={form.getValues("employerOffersCarAllowance")}
              onChange={(checked) => {
                form.setValue("employerOffersCarAllowance", checked);
              }}
            />
          }
        />
        <div className="ps-[40px]">
          <Controller
            control={form.control}
            name="employerOffersCarAllowanceAmount"
            render={({ field }) => (
              <NumberFormatBase
                value={field.value}
                disabled={!form.watch("employerOffersCarAllowance")}
                name="employerOffersCarAllowanceAmount"
                className="w-full"
                customInput={TextField}
                error={!!form.formState.errors.employerOffersCarAllowanceAmount}
                errorText={form.formState.errors.employerOffersCarAllowanceAmount?.message as string}
                onFocus={(e) => {
                  e.target.select();
                }}
                format={currencyFormatterNoDecimals}
                onValueChange={(values) => {
                  field.onChange(values.value ? parseInt(values.value, 10) : null);
                  form.trigger("employerOffersCarAllowanceAmount");
                }}
                leadingIcon="attach_money"
                label="Amount of Car Allowance"
                placeholder="Amount of Car Allowance"
              />
            )}
          />
        </div>
        <ListItem
          headline="My employer offers clothing allowance"
          leading={
            <Checkbox
              name="employerOffersClothingAllowance"
              checked={form.getValues("employerOffersClothingAllowance")}
              onChange={(checked) => {
                form.setValue("employerOffersClothingAllowance", checked);
              }}
            />
          }
        />
        <div className="ps-[40px]">
          <Controller
            control={form.control}
            name="employerOffersClothingAllowanceAmount"
            render={({ field }) => (
              <NumberFormatBase
                value={field.value}
                disabled={!form.watch("employerOffersClothingAllowance")}
                name="employerOffersClothingAllowanceAmount"
                className="w-full"
                customInput={TextField}
                error={!!form.formState.errors.employerOffersClothingAllowanceAmount}
                errorText={form.formState.errors.employerOffersClothingAllowanceAmount?.message as string}
                onFocus={(e) => {
                  e.target.select();
                }}
                format={currencyFormatterNoDecimals}
                onValueChange={(values) => {
                  field.onChange(values.value ? parseInt(values.value, 10) : null);
                  form.trigger("employerOffersClothingAllowanceAmount");
                }}
                leadingIcon="attach_money"
                label="Amount of Clothing Allowance"
                placeholder="Amount of Clothing Allowance"
              />
            )}
          />
        </div>
        <ListItem
          headline="I participate in Golf Retirement Plus"
          leading={
            <Checkbox
              name="participateInGrp"
              checked={form.getValues("participateInGrp")}
              onChange={(checked) => {
                form.setValue("participateInGrp", checked);
              }}
            />
          }
        />

        <div className="col-span-full">
          <p className="text-title-medium mb-2">Other Employment Benefits Description</p>
          <Controller
            control={form.control}
            name="otherEmploymentBenefitsDescription"
            render={({ field }) => (
              <TextField
                onChangeText={(text) => {
                  field.onChange(text);
                }}
                value={field.value || ""}
                label="Other Employment Benefits Description"
                type="textarea"
                rows={6}
                className="w-full"
              />
            )}
          />
        </div>
      </div>
    </div>
  );
}
